import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { api, setTokenProvider } from './api/client.js'
import { firebaseEnabled, getFirebaseAuth } from './firebase.js'

/**
 * Auth context.
 * - Firebase configured: Google / Apple / Facebook / email via Firebase Auth,
 *   the API gets the Firebase ID token.
 * - Otherwise (dev): email form signs in as `dev:<name before @>`.
 */
const AuthContext = createContext(null)
const DEV_KEY = 'urbanpurr.devUid'
const devEnabled = !firebaseEnabled && import.meta.env.VITE_AUTH_DEV === 'true'

const readDevUser = () => {
  try {
    const uid = localStorage.getItem(DEV_KEY)
    return uid ? { uid, email: `${uid}@dev.local`, name: uid } : null
  } catch {
    return null
  }
}

setTokenProvider(async () => {
  if (firebaseEnabled) {
    const fb = await getFirebaseAuth()
    return fb?.auth.currentUser ? fb.auth.currentUser.getIdToken() : null
  }
  const user = readDevUser()
  return user ? `dev:${user.uid}` : null
})

const toUser = (u) => u && { uid: u.uid, email: u.email, name: u.displayName || u.email }

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => (firebaseEnabled ? null : readDevUser()))
  const [ready, setReady] = useState(!firebaseEnabled)
  const [profile, setProfile] = useState(null)

  // Backend profile (is_admin, locale, meal count, title). Reloaded whenever
  // the signed-in user changes, and on demand after feeding / settings.
  const [profileVersion, setProfileVersion] = useState(0)
  useEffect(() => {
    let cancelled = false
    const load = user ? api.me() : Promise.resolve(null)
    load
      .then((p) => !cancelled && setProfile(p))
      .catch(() => !cancelled && setProfile(null))
    return () => {
      cancelled = true
    }
  }, [user, profileVersion])

  useEffect(() => {
    if (!firebaseEnabled) return
    let unsub = () => {}
    getFirebaseAuth().then(({ auth, m }) => {
      unsub = m.onAuthStateChanged(auth, (u) => {
        setUser(toUser(u))
        setReady(true)
      })
    })
    return () => unsub()
  }, [])

  const value = useMemo(() => {
    const popup = async (providerName) => {
      const { auth, m } = await getFirebaseAuth()
      const provider =
        providerName === 'google'
          ? new m.GoogleAuthProvider()
          : providerName === 'apple'
            ? new m.OAuthProvider('apple.com')
            : new m.FacebookAuthProvider()
      await m.signInWithPopup(auth, provider)
    }
    const devLogin = (email) => {
      const uid = email.split('@')[0].toLowerCase().replace(/[^a-z0-9_-]/g, '') || 'user1'
      localStorage.setItem(DEV_KEY, uid)
      setUser(readDevUser())
    }
    return {
      user,
      ready,
      profile,
      isAdmin: Boolean(profile?.is_admin),
      refreshProfile: () => setProfileVersion((v) => v + 1),
      firebaseEnabled,
      devEnabled,
      loginWithProvider: popup,
      loginWithEmail: async (email, password) => {
        if (!firebaseEnabled) return devLogin(email)
        const { auth, m } = await getFirebaseAuth()
        await m.signInWithEmailAndPassword(auth, email, password)
      },
      register: async (email, password) => {
        if (!firebaseEnabled) return devLogin(email)
        const { auth, m } = await getFirebaseAuth()
        await m.createUserWithEmailAndPassword(auth, email, password)
      },
      resetPassword: async (email) => {
        const { auth, m } = await getFirebaseAuth()
        await m.sendPasswordResetEmail(auth, email)
      },
      /** Account and its data on the server, then sign out locally. */
      deleteAccount: async () => {
        await api.deleteMe()
        if (firebaseEnabled) {
          const { auth, m } = await getFirebaseAuth()
          if (auth.currentUser) await m.signOut(auth)
        } else {
          localStorage.removeItem(DEV_KEY)
          setUser(null)
        }
      },
      logout: async () => {
        if (firebaseEnabled) {
          const { auth, m } = await getFirebaseAuth()
          await m.signOut(auth)
        } else {
          localStorage.removeItem(DEV_KEY)
          setUser(null)
        }
      },
    }
  }, [user, ready, profile])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext)
